<script setup lang="ts">
import { useDateTime } from '@ai/ui-components'
import { useApiKeysListQuery, useDeleteApiKeyMutation } from '@/composables/queries/api-keys'
import type { ApiKey } from 'shared/src'
import CreateApiKeyModal from '@/components/BusinessLogic/Modal/CreateApiKeyModal.vue'
import { useToast } from '@ai/ui-components/toast'
import { LIST_ACTION_ICONS } from '@/components/BusinessLogic/TableActions'

const { t } = useI18n({ useScope: 'global' })
const { formatDateTime } = useDateTime()
const toast = useToast()

const { data, isLoading } = useApiKeysListQuery()
const keys = computed(() => data.value ?? [])
const { mutateAsync: deleteApiKey, isPending: isDeleting } = useDeleteApiKeyMutation()

const selectedForDelete = ref<ApiKey>()
const openDeleteModal = ref(false)
const openCreateModal = ref(false)

const onCreate = () => {
  openCreateModal.value = true
}

const keyColumns = computed(() => [
  { key: 'name', label: t('name') },
  { key: 'createdAt', label: t('created_at') },
  { key: 'start', label: t('key') },
])

const selectedForDeleteKeyName = computed(() => selectedForDelete.value?.name ?? '')

const maskApiKey = (key: string | null | undefined) => {
  if (!key || key.length < 2) return key
  const first = key.charAt(0)
  const last = key.charAt(key.length - 1)
  const masked = '*'.repeat(Math.max(3, key.length - 2))
  return `${first}${masked}${last}`
}

const toggleDeleteModal = (item: ApiKey) => {
  selectedForDelete.value = item
  openDeleteModal.value = !openDeleteModal.value
}

const handleDelete = async (): Promise<boolean> => {
  if (!selectedForDelete.value) return false

  try {
    await deleteApiKey(selectedForDelete.value.keyId)
    toast.success(t('api_key_permanently_deleted'))
    openDeleteModal.value = false
    return true
  } catch (error) {
    console.error('Error deleting API key:', error)
    toast.error(t('api_key_not_deleted_try_again'), { title: t('error_occurred') })
    return false
  }
}

const handleCancelDelete = (): boolean => {
  openDeleteModal.value = false
  return true
}

const getActions = (item: ApiKey) => [
  {
    label: t('delete'),
    value: 'delete',
    icon: LIST_ACTION_ICONS.deleteDanger,
    handler: () => {
      toggleDeleteModal(item)
    },
    danger: true,
    dataTestid: 'action-delete',
  },
]
</script>

<template>
  <OCardAdvanced
    elevation="S"
    border-radius="XL"
    content-class="border border-neutral-5"
    inline
    :title="t('api_keys')"
    :subtitle="t('api_keys_subtitle')"
  >
    <div class="col-span-2 max-h-[400px]">
      <div class="mb-6 flex justify-end">
        <OButton size="M" :label="t('create')" @click="onCreate" data-testid="api-create" />
      </div>

      <OTable
        class="max-h-[calc(100%-3.5rem)]"
        dataTestid="api_keys_table"
        :columns="keyColumns"
        :model-value="keys"
        sortable
        resizable
        column-resize-mode="indicator"
        :loading="isLoading"
        default-sort-key="createdAt"
        default-sort-order="desc"
      >
        <template #createdAt="{ item }">
          <OOverflowTooltip
            :content="
              formatDateTime({
                format: 'dateTime',
                value: new Date(item.createdAt).toISOString(),
                fallback: '-',
              })
            "
          >
            {{
              formatDateTime({
                format: 'dateTime',
                value: new Date(item.createdAt).toISOString(),
                fallback: '-',
              })
            }}
          </OOverflowTooltip>
        </template>
        <template #start="{ item }">
          <OOverflowTooltip :content="maskApiKey(item.start) ?? ''">
            <span v-mp-no-track class="font-mono">{{ maskApiKey(item.start) }}</span>
          </OOverflowTooltip>
        </template>
        <template #actions="{ item }">
          <OListActions data-testid="row-action-more" small :actions="getActions(item as ApiKey)" />
        </template>
      </OTable>
    </div>
  </OCardAdvanced>

  <CreateApiKeyModal v-model:open="openCreateModal" />

  <!-- Delete confirmation modal -->
  <OModal
    v-model:open="openDeleteModal"
    :title="t('confirm_deletion')"
    :secondary-action-title="t('cancel')"
    :primary-action-title="t('delete_permanently')"
    :secondary-action-promise="handleCancelDelete"
    :primary-action-promise="handleDelete"
    :primary-action-loading="isDeleting"
    primary-action-enabled
    secondary-action-enabled
    danger
    size="medium"
  >
    <template #content>
      <div class="text-neutral-1">
        <i18n-t keypath="delete_api_key_confirmation" tag="p" class="typography-body-2">
          <span class="typography-body-2-strong">{{ selectedForDeleteKeyName }}</span>
        </i18n-t>
      </div>
    </template>
  </OModal>
</template>
